import React, { useContext, useEffect, useMemo, useState } from 'react';
import { IconButton, IIconProps, Label, Text } from '@fluentui/react';
import {
  Breadcrumb,
  BreadcrumbButton,
  BreadcrumbDivider,
  BreadcrumbItem,
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tooltip,
  isTruncatableBreadcrumbContent,
  partitionBreadcrumbItems,
  truncateBreadcrumbLongName,
} from '@fluentui/react-components';
import { MoreHorizontalRegular } from '@fluentui/react-icons';
import { ReactReduxContext } from 'react-redux';
import { Store } from 'redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AppState } from '../../../redux/reducers';
import * as StorageBrowserStore from '../../../redux/middleware/StorageBrowser.Middleware';
import { StorageOwnerNames, Translate } from '../../../functions/StorageBrowser.Functions';
import {
  BreadcrumbItemDef,
  buildPageBreadcrumbs,
  getParentHref,
  getResolvableEntityRefs,
  hasAppHistory,
  resolveBackTarget,
  selectPageEntityNames,
} from './PageHeaderBreadcrumbs.Functions';
import './PageHeader.Styles.css';

export type { BreadcrumbItemDef } from './PageHeaderBreadcrumbs.Functions';

export interface PageHeaderProps {
  title: string;
  subtitle?: string | undefined;
  /** Shown above the title only when there is no breadcrumb trail. */
  eyebrow?: string;
  /** Trail to show instead of the one derived from the route. */
  breadcrumbs?: BreadcrumbItemDef[];
  hideBreadcrumbs?: boolean;
  /** Fixed destination of the back button. */
  backTo?: string;
}

/** Trails longer than this collapse their middle levels into a "…" menu. */
const MAX_DISPLAYED_ITEMS = 4;
/** Longer labels are cut with "..." and the full text goes to a tooltip. */
const MAX_LABEL_LENGTH = 32;

const EMPTY_NAMES: StorageOwnerNames = {};

/**
 * Entity names known by the store. PageHeader is rendered by every page, also in tests without a
 * redux Provider: the store is optional and, when missing, the trail uses the "{Entidad} {id}" labels.
 */
const useOptionalStore = (): Store<AppState> | undefined =>
  (useContext(ReactReduxContext) as { store?: Store<AppState> } | null)?.store;

const usePageEntityNames = (store: Store<AppState> | undefined): StorageOwnerNames => {
  const [names, setNames] = useState<StorageOwnerNames>(() =>
    store ? selectPageEntityNames(store.getState()) : EMPTY_NAMES
  );
  useEffect(() => {
    if (!store) return undefined;
    const update = () => setNames(selectPageEntityNames(store.getState()));
    update();
    return store.subscribe(update);
  }, [store]);
  return names;
};

const BreadcrumbLabel = ({ label }: { label: string }) =>
  isTruncatableBreadcrumbContent(label, MAX_LABEL_LENGTH) ? (
    <Tooltip content={label} relationship="label" withArrow>
      <span>{truncateBreadcrumbLongName(label, MAX_LABEL_LENGTH)}</span>
    </Tooltip>
  ) : (
    <>{label}</>
  );

interface PageHeaderBreadcrumbProps {
  items: BreadcrumbItemDef[];
  onNavigate: (href: string) => void;
}

export const PageHeaderBreadcrumb = ({ items, onNavigate }: PageHeaderBreadcrumbProps) => {
  const { t } = useTranslation();
  const { startDisplayedItems, overflowItems, endDisplayedItems } = partitionBreadcrumbItems<BreadcrumbItemDef>({
    items,
    maxDisplayedItems: MAX_DISPLAYED_ITEMS,
  });
  const lastKey = items[items.length - 1]?.key;

  const renderItem = (item: BreadcrumbItemDef) => {
    const isCurrent = item.key === lastKey;
    const content = <BreadcrumbLabel label={item.label} />;
    return (
      <React.Fragment key={item.key}>
        <BreadcrumbItem>
          {isCurrent ? (
            <BreadcrumbButton current className="ph-crumb ph-crumb--current">
              {content}
            </BreadcrumbButton>
          ) : item.href ? (
            <BreadcrumbButton
              className="ph-crumb ph-crumb--link"
              href={item.href}
              onClick={(event: React.MouseEvent) => {
                // Plain click: client-side navigation. Ctrl/Cmd/middle click keep the browser behavior.
                if (event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0) return;
                event.preventDefault();
                onNavigate(item.href as string);
              }}
            >
              {content}
            </BreadcrumbButton>
          ) : (
            <span className="ph-crumb ph-crumb--group">{content}</span>
          )}
        </BreadcrumbItem>
        {!isCurrent && <BreadcrumbDivider className="ph-crumbDivider" />}
      </React.Fragment>
    );
  };

  return (
    <Breadcrumb size="small" className="ph-breadcrumb" aria-label={t('pageHeader.breadcrumbs.ariaLabel')}>
      {startDisplayedItems.map(renderItem)}
      {overflowItems && overflowItems.length > 0 && (
        <>
          <BreadcrumbItem>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button
                  appearance="subtle"
                  size="small"
                  className="ph-crumbOverflow"
                  icon={<MoreHorizontalRegular />}
                  aria-label={t('pageHeader.breadcrumbs.more')}
                />
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  {overflowItems.map((item) => (
                    <MenuItem
                      key={item.key}
                      disabled={!item.href}
                      onClick={() => item.href && onNavigate(item.href)}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </MenuPopover>
            </Menu>
          </BreadcrumbItem>
          <BreadcrumbDivider className="ph-crumbDivider" />
        </>
      )}
      {endDisplayedItems?.map(renderItem)}
    </Breadcrumb>
  );
};

export const PageHeader = (props: PageHeaderProps) => {
  const BackIcon: IIconProps = { iconName: 'ChevronLeftSmall' };

  const { t } = useTranslation();
  const translate = t as unknown as Translate;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const store = useOptionalStore();
  const names = usePageEntityNames(store);

  // Protocols, settlements, providers, sponsors and practitioners shown by id get their name
  // through the same lookup the files explorer uses (cached in state.StorageBrowser.entityNames).
  useEffect(() => {
    if (!store || props.breadcrumbs || props.hideBreadcrumbs) return;
    const refs = getResolvableEntityRefs(pathname);
    if (refs.length === 0) return;
    try {
      const pending = store.dispatch(StorageBrowserStore.ActionCreators.ResolveEntityNames(refs) as never) as unknown;
      (pending as Promise<void> | undefined)?.catch?.(() => undefined);
    } catch {
      // stores without thunk (some tests): the trail keeps the "{Entidad} {id}" labels
    }
  }, [store, pathname, props.breadcrumbs, props.hideBreadcrumbs]);

  const items = useMemo(
    () => (props.hideBreadcrumbs ? [] : props.breadcrumbs ?? buildPageBreadcrumbs(pathname, translate, names)),
    [props.hideBreadcrumbs, props.breadcrumbs, pathname, names, t] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleBackClick = () => {
    const target = resolveBackTarget({
      backTo: props.backTo,
      hasAppHistory: hasAppHistory(window.history.state),
      parentHref: getParentHref(props.breadcrumbs ?? buildPageBreadcrumbs(pathname, translate, names)),
    });
    if (target === -1) navigate(-1);
    else navigate(target);
  };

  return (
    <div className="PageHeader">
      <IconButton
        iconProps={BackIcon}
        onClick={handleBackClick}
        ariaLabel={t('pageHeader.back')}
        title={t('pageHeader.back')}
      />
      <div className="ph-textBlock">
        {items.length > 0 ? (
          <PageHeaderBreadcrumb items={items} onNavigate={(href) => navigate(href)} />
        ) : (
          props.eyebrow && <span className="ph-eyebrow">{props.eyebrow}</span>
        )}
        <Label className="ph-title">{props.title}</Label>
        {props.subtitle && <Text className="ph-subtitle">{props.subtitle}</Text>}
      </div>
    </div>
  );
};
